import {Plural, Trans, useLingui} from "@lingui/react/macro";
import {useMemo, useState} from "react";
import {useNavigate} from "react-router";
import type {GroupRepo, ItemEntry, ItemId} from "../../models/GroupRepo";
import {blueTheme} from "../../styles/themes/color-themes.css";
import {Box, Col, css, Row, Text} from "../../ui/Box";
import {Input} from "../../ui/Input";
import {useSuggestions} from "../items/AddItem";
import {ItemPillLayout, MiniPill} from "../items/ItemPill";
import {useRecipeIdsByItemId} from "./use-recipe-ids-by-item-id";

type UsedItem = {item: ItemEntry; recipeCount: number};

type IngredientFilterPickerProps = {
  groupRepo: GroupRepo;
  // already in the filter; recipes have to use all of them
  pickedItemIds: ItemId[];
  onPick: (itemId: ItemId) => void;
};

/**
 * Lists the items that recipes use, most used first, to add one of them to the recipe filter.
 * With items already picked, it only offers and counts items of recipes that use all of those, so
 * every choice still leaves some recipes.
 */
const IngredientFilterPicker = (props: IngredientFilterPickerProps) => {
  const {groupRepo, pickedItemIds, onPick} = props;
  const {t} = useLingui();
  const navigate = useNavigate();
  const [searchVal, setSearchVal] = useState("");
  const items = groupRepo.Items.useGetAll();
  const recipeIdsByItemId = useRecipeIdsByItemId(groupRepo);

  const usedItems = useMemo(() => {
    const picked = new Set(pickedItemIds);
    // recipes that use every picked item
    const candidates =
      pickedItemIds.length === 0
        ? null
        : new Set(
            [...(recipeIdsByItemId.get(pickedItemIds[0]) ?? [])].filter((recipeId) =>
              pickedItemIds.every((itemId) => recipeIdsByItemId.get(itemId)?.has(recipeId))
            )
          );
    const list: UsedItem[] = [];
    for (const [itemId, recipeIds] of recipeIdsByItemId) {
      if (picked.has(itemId) || !Object.hasOwn(items, itemId)) continue;
      const recipeCount = candidates
        ? [...recipeIds].filter((recipeId) => candidates.has(recipeId)).length
        : recipeIds.size;
      if (recipeCount > 0) list.push({item: items[itemId], recipeCount});
    }
    return list.sort(
      (a, b) => b.recipeCount - a.recipeCount || a.item.name.localeCompare(b.item.name)
    );
  }, [items, recipeIdsByItemId, pickedItemIds]);

  const matches = useSuggestions(searchVal.trim(), usedItems, ({item}) => item.name);
  const shown = searchVal.trim() ? matches : usedItems;

  const handlePick = (itemId: ItemId) => {
    onPick(itemId);
    navigate("..");
  };

  return (
    <Col
      sp="24"
      height="_addItemDrawerHeight"
      maxWidth="formWidth"
      width="100%"
      mr="auto"
      ml="auto"
    >
      <Input
        type="text"
        value={searchVal}
        onChange={setSearchVal}
        placeholder={t`Search ingredients`}
        enterKeyHint="search"
        autoComplete="off"
        data-drawer-autofocus
      />
      <Box overflow="auto" flex="auto">
        {shown.length === 0 ? (
          <Text size="12" color="secondary">
            {usedItems.length === 0 && pickedItemIds.length > 0 ? (
              <Trans>The matching recipes have no other ingredients</Trans>
            ) : usedItems.length === 0 ? (
              <Trans>No recipe has ingredients yet</Trans>
            ) : (
              <Trans>No matching ingredients</Trans>
            )}
          </Text>
        ) : (
          <Col sp="8">
            {shown.map(({item, recipeCount}) => (
              <ItemPillLayout
                key={item.id}
                emoji={item.emoji}
                theme={blueTheme}
                name={item.name}
                onClick={() => handlePick(item.id)}
                className={css({flex: "none"})}
              >
                <Row align="center" pr="8">
                  <MiniPill>
                    <Plural value={recipeCount} one="# recipe" other="# recipes" />
                  </MiniPill>
                </Row>
              </ItemPillLayout>
            ))}
          </Col>
        )}
      </Box>
    </Col>
  );
};

export default IngredientFilterPicker;
